import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import API_BASE from '../apiBase';

export default function useProjectCard(projectId) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fetchedProjectIdRef = useRef(null);
  const isFetchingRef = useRef(false);

  const fetchAnalytics = useCallback(async () => {
    if (!projectId) return;
    
    // Prevent duplicate calls for the same project
    if (fetchedProjectIdRef.current === projectId) {
      return;
    }
    
    // Prevent concurrent calls
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE}/api/projects-analytics/${projectId}`);
      setAnalytics(response.data);
      fetchedProjectIdRef.current = projectId;
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (error.response?.status === 404) {
        setError('Project analytics not found.');
      } else {
        setError(`Failed to fetch project analytics: ${error.response?.data?.error || error.message}`);
      }
      setAnalytics(null);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [projectId]);

  useEffect(() => {
    // Only fetch if projectId changed
    if (projectId && fetchedProjectIdRef.current !== projectId) {
      fetchAnalytics();
    }
  }, [projectId, fetchAnalytics]);

  return {
    analytics,
    loading,
    error,
    refetch: fetchAnalytics,
  };
}
